import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Type } from '@spotify-internal/encore-creator-web';
import {
  screenSmMax,
  screenMdMin,
  spacer8,
  spacer24,
  spacer40,
  black,
} from '@spotify-internal/encore-foundation';
import { isInternalUrl, getRelativeUrl } from '../../HomeHelper';
import { CTABtnAsLink, CTABtnArrow } from '../../Style';
import { formatString } from '../../formatters';
import { webgateFetch } from '@mrkt/features/webgate-fetch';
import {
  CardFadeInAnimation,
  CardGrayAnimation,
  CardShadowAnimation,
} from './Animation';
import CardHeader from './CardHeader';

const CardCtaBtn = styled(CTABtnAsLink)`
  /* apply "stretched link" such that card container has same hover as button */
  transform: none;
  /* https://ghe.spotify.net/tape/creator-tape/blob/master/src/styles/mixins/buttons.js#L94-L104 */
  &::after {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1;
    cursor: pointer; /* homecard specific cursor pointer */
    content: '';
  }
`;

const CardContainer = styled(CardFadeInAnimation)`
  ${props =>
    props.displayType === 'ICON' || props.displayType === 'STAT'
      ? `border-top: 1px solid ${black}`
      : null};
  @media (max-width: ${screenSmMax}) {
    margin-bottom: ${spacer24};
  }

  @media (min-width: ${screenMdMin}) {
    margin-bottom: ${spacer40};
  }
`;

const CardTextArea = styled.div`
  padding: ${spacer24} ${spacer24} 0 ${spacer24};
`;

const CardTitle = styled(Type.h1).attrs({
  variant: Type.heading3,
  weight: Type.bold,
  condensed: true,
})``;

const CardBody = styled(Type.p).attrs({
  variant: Type.body2,
  condensed: true,
})`
  padding-top: ${spacer8};
`;

class Card extends PureComponent {
  static propTypes = {
    uuid: PropTypes.string,
    resourceUri: PropTypes.string,
    cardSource: PropTypes.string,
    notificationClass: PropTypes.string,
    clickExpirationBuffer: PropTypes.shape({
      day: PropTypes.number,
      hour: PropTypes.number,
      minute: PropTypes.number,
      second: PropTypes.number,
    }),
    displayCard: PropTypes.shape({
      header: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      subtitle: PropTypes.string.isRequired,
      callToAction: PropTypes.shape({
        text: PropTypes.string.isRequired,
        url: PropTypes.string.isRequired,
      }),
      displayType: PropTypes.oneOf([
        'COVER',
        'ICON',
        'INFO',
        'STAT',
        'UNKNOWN_DISPLAY_TYPE',
      ]).isRequired,
      headerType: PropTypes.oneOf([
        'IMAGE_URL',
        'TEXT',
        'DELTA',
        'UNKNOWN_HEADER_TYPE',
      ]).isRequired,
      fallbackImageUrl: PropTypes.string,
      cardColorList: PropTypes.arrayOf(
        PropTypes.shape({
          preset: PropTypes.string,
          colorHex: PropTypes.string,
        }),
      ),
    }),
    history: PropTypes.shape({
      push: PropTypes.func,
    }),
    artist: PropTypes.shape({
      id: PropTypes.string.isRequired,
    }).isRequired,
    viewport: PropTypes.number,
    cardNum: PropTypes.number.isRequired,
    totalCardNum: PropTypes.number.isRequired,
    tracker: PropTypes.shape({
      makeHomeCardEvent: PropTypes.func,
      trackImpression: PropTypes.func,
      trackInteraction: PropTypes.func,
    }),
  };

  componentDidMount() {
    const {
      resourceUri,
      displayCard,
      cardSource,
      uuid,
      notificationClass,
      clickExpirationBuffer,
    } = this.props;

    this.props.tracker.trackImpression(
      this.props.tracker.makeHomeCardEvent(
        uuid,
        cardSource,
        displayCard.callToAction ? displayCard.callToAction.url : '',
        resourceUri,
        notificationClass,
      ),
    );
  }

  handleCardClick = (
    ev,
    artistId,
    artistUri,
    cardSource,
    ctaUrl,
    cardId,
    notificationClass,
    clickExpirationBuffer,
  ) => {
    ev.preventDefault();
    ev.stopPropagation();

    this.props.tracker.trackInteraction(
      this.props.tracker.makeHomeCardEvent(
        cardId,
        cardSource,
        ctaUrl,
        artistUri,
        notificationClass,
      ),
    );

    /**
     * Default value for clickExpirationBuffer is (0, 0, 0, 0),
     * Only attempt to expire a card if any field in clickExpirationBuffer
     * is set > 0.
     */
    if (
      clickExpirationBuffer &&
      Object.values(clickExpirationBuffer).some(x => x > 0)
    ) {
      webgateFetch(
        `https://generic.wg.spotify.com/sfx-home-cards/v1/card/${cardId}/expire-buffered`,
        {
          method: 'PUT',
        },
      );
    }

    isInternalUrl(ctaUrl)
      ? this.props.history.push(getRelativeUrl(ctaUrl))
      : window.open(ctaUrl, '_blank');

    return null;
  };

  render() {
    const {
      artist,
      resourceUri,
      cardSource,
      uuid,
      notificationClass,
      cardNum,
      totalCardNum,
      viewport,
      clickExpirationBuffer,
      displayCard,
    } = this.props;

    const CardHeaderProps = {
      displayType: displayCard.displayType,
      header: displayCard.header,
      fallbackImageUrl: displayCard.fallbackImageUrl,
      cardColorList: displayCard.cardColorList,
      viewport,
      headerType: displayCard.headerType,
    };

    const hasCta = Boolean(displayCard.callToAction);

    return (
      <CardGrayAnimation cardNum={cardNum}>
        <CardShadowAnimation cardNum={cardNum} totalCardNum={totalCardNum}>
          <CardContainer
            displayType={displayCard.displayType}
            cardNum={cardNum}
            onClick={
              hasCta
                ? ev =>
                    this.handleCardClick(
                      ev,
                      artist.id,
                      resourceUri,
                      cardSource,
                      displayCard.callToAction.url,
                      uuid,
                      notificationClass,
                      clickExpirationBuffer,
                    )
                : undefined
            }
          >
            <CardHeader {...CardHeaderProps} />
            <CardTextArea>
              <CardTitle id={uuid}>{formatString(displayCard.title)}</CardTitle>
              <CardBody>{formatString(displayCard.subtitle)}</CardBody>
              {hasCta ? (
                <CardCtaBtn
                  to="#"
                  onClick={ev => ev.preventDefault()}
                  aria-describedby={uuid}
                >
                  {formatString(displayCard.callToAction.text)}
                  <CTABtnArrow focusable="false" aria-hidden="true" />
                </CardCtaBtn>
              ) : null}
            </CardTextArea>
          </CardContainer>
        </CardShadowAnimation>
      </CardGrayAnimation>
    );
  }
}
/* eslint-disable-next-line import/no-default-export */
export default Card;
